/**
 * Employee and department API surface (client feedback 03 Sep: full CRUD on the employee
 * master, added on top of the review module's read-only `ReviewEmployee` picker).
 *
 * Both routers are gated on the `performance_reviews` matrix cell -- there is still no
 * dedicated `employees` row in the PRD's fixed table, and the review module already owns
 * the person record reviews point at. That cell gives Sales Manager `F` on paper, but the
 * router narrows create/update/delete on employees to HR Manager and Super Admin
 * underneath it (see the router's own comment); a sales manager who tries anyway gets a
 * 403 back from the server, which the write dialogs surface the same way every other
 * form here surfaces a rejected write -- there is no finer matrix cell to hide the button
 * behind on this screen.
 */

import { API_PREFIX, apiBlob, apiFetch, apiGet, type Page } from '@/api/client'
import type { ExportFormat } from '@/components/ExportButton'

export const EMPLOYMENT_TYPES = ['full_time', 'part_time', 'contract'] as const
export type EmploymentType = (typeof EMPLOYMENT_TYPES)[number]

export const PAY_FREQUENCIES = ['monthly', 'biweekly', 'weekly'] as const
export type PayFrequency = (typeof PAY_FREQUENCIES)[number]

export const EMPLOYEE_STATUSES = ['active', 'on_leave', 'terminated'] as const
export type EmployeeStatus = (typeof EMPLOYEE_STATUSES)[number]

export interface EmergencyContact {
  name?: string
  phone?: string
}

export interface EmployeeCreate {
  user_id: string
  employee_code: string
  department_id?: string | null
  position: string
  manager_id?: string | null
  location_id?: string | null
  hire_date: string
  termination_date?: string | null
  employment_type?: string
  salary: number | string
  pay_frequency?: string
  tax_id?: string | null
  bank_account?: string | null
  emergency_contact?: EmergencyContact | null
  status?: string
  sales_target_monthly?: number | string | null
}

/** Every `EmployeeCreate` field but `user_id`: swapping the account a record belongs to
 *  is not an edit, it is handing a payroll history to a different person. */
export type EmployeeUpdate = Omit<EmployeeCreate, 'user_id'>

export interface EmployeeRead {
  id: string
  user_id: string
  full_name: string | null
  employee_code: string
  department_id: string | null
  department_name: string | null
  position: string
  manager_id: string | null
  location_id: string | null
  hire_date: string
  termination_date: string | null
  employment_type: string
  pay_frequency: string
  status: string
  sales_target_monthly: string | null
  created_at: string
  updated_at: string
  /** `null` on all four when this viewer may not see them -- see the module notes above. */
  salary: string | null
  tax_id: string | null
  bank_account: string | null
  emergency_contact: EmergencyContact | null
}

export interface EmployeeFilters {
  page?: number
  q?: string
  department_id?: string
  status?: string
}

function query(filters: EmployeeFilters): string {
  const params = new URLSearchParams()
  if (filters.page && filters.page > 1) params.set('page', String(filters.page))
  if (filters.q) params.set('q', filters.q)
  if (filters.department_id) params.set('department_id', filters.department_id)
  if (filters.status) params.set('status', filters.status)
  const qs = params.toString()
  return qs ? `?${qs}` : ''
}

export const listEmployees = (filters: EmployeeFilters = {}) =>
  apiFetch<Page<EmployeeRead>>(`${API_PREFIX}/employees${query(filters)}`)

export const getEmployee = (id: string) => apiGet<EmployeeRead>(`${API_PREFIX}/employees/${id}`)

/** Just enough of `inventory.Location` for the employee form's picker. Fetched from an
 *  hr-owned endpoint, not `@/features/orders/api`'s `listLocations` -- `hr_manager` has no
 *  rights on the `inventory` matrix cell that one is gated on. */
export interface EmployeeLocationOption {
  id: string
  code: string
  name: string
}

export const listEmployeeLocations = () =>
  apiGet<EmployeeLocationOption[]>(`${API_PREFIX}/employees/locations`)

// Unlike every other export endpoint in the app (including this module's own
// `customers/export`), this one's format query key is `format`, not `fmt` -- see the
// router's `Query(alias="format")`. Matched here on purpose; do not "fix" to `fmt`.
export const exportEmployees = (filters: EmployeeFilters, format: ExportFormat) => {
  const qs = query(filters)
  return apiBlob(`${API_PREFIX}/employees/export${qs ? `${qs}&format=${format}` : `?format=${format}`}`)
}

export const createEmployee = (data: EmployeeCreate) =>
  apiGet<EmployeeRead>(`${API_PREFIX}/employees`, { method: 'POST', body: JSON.stringify(data) })

export const updateEmployee = (id: string, data: EmployeeUpdate) =>
  apiGet<EmployeeRead>(`${API_PREFIX}/employees/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  })

/** Soft delete -- see the router's own summary. */
export const deleteEmployee = (id: string) =>
  apiFetch<void>(`${API_PREFIX}/employees/${id}`, { method: 'DELETE' })

export interface DepartmentCreate {
  name: string
  description?: string | null
  head_id?: string | null
}

export type DepartmentUpdate = Partial<DepartmentCreate>

export interface DepartmentRead {
  id: string
  name: string
  description: string | null
  head_id: string | null
  created_at: string
  updated_at: string
}

export const listDepartments = (q?: string) =>
  apiFetch<Page<DepartmentRead>>(`${API_PREFIX}/departments${q ? `?q=${encodeURIComponent(q)}` : ''}`)

export const getDepartment = (id: string) =>
  apiGet<DepartmentRead>(`${API_PREFIX}/departments/${id}`)

export const createDepartment = (data: DepartmentCreate) =>
  apiGet<DepartmentRead>(`${API_PREFIX}/departments`, {
    method: 'POST',
    body: JSON.stringify(data),
  })

export const updateDepartment = (id: string, data: DepartmentUpdate) =>
  apiGet<DepartmentRead>(`${API_PREFIX}/departments/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  })

/** No soft-delete note on this one in the router -- a department with employees still
 *  pointing at it is the service's problem, not this client's. */
export const deleteDepartment = (id: string) =>
  apiFetch<void>(`${API_PREFIX}/departments/${id}`, { method: 'DELETE' })
